import { getComponentQuantityInSystem } from 'projectSections/designPanels/designRules/utils'
import { handleAddComponent } from 'projectSections/sections/design/util/handleAddComponent'
import type { StudioSystemType } from 'types/global'

/**
 * Syncs the quantity of an "other" component in the system to the target quantity.
 * Always consolidates to a single OsOther per code (avoids multiple entries when
 * ComponentCollection doesn't merge, e.g. for components without slotKey).
 * - If newQuantity > current: update existing OsOther to newQuantity, or add one if none
 * - If newQuantity < current: update first OsOther to newQuantity, delete duplicates
 * - If newQuantity === 0: removes all OsOther instances with this code
 *
 * Uses MultiCmdsCommand so quantity update + deletions are a single undo step.
 */
export const syncQuantityInSystem = (
  system: StudioSystemType,
  code: string,
  activationId: number,
  newQuantity: number
): void => {
  const currentQuantity = getComponentQuantityInSystem(system, 'other', code)
  const othersWithCode = system.others().filter((c) => c.code === code)

  if (newQuantity > currentQuantity) {
    if (othersWithCode.length === 0) {
      handleAddComponent('other', system.uuid, { otherId: activationId, quantity: newQuantity })
      return
    }
    executeFlattenCommands(othersWithCode, newQuantity)
  } else if (newQuantity < currentQuantity) {
    if (newQuantity === 0) {
      executeDeleteAllCommands(othersWithCode)
    } else {
      const [first, ...duplicatesToRemove] = othersWithCode
      if (first) {
        executeFlattenCommands(othersWithCode, newQuantity)
      }
    }
  }
}

function executeFlattenCommands(othersWithCode: Array<{ uuid: string; quantity?: number }>, newQuantity: number): void {
  const [first, ...duplicatesToRemove] = othersWithCode
  const firstObj = window.editor?.objectByUuid(first.uuid)
  if (!firstObj) return

  const cmds = [
    new window.SetValueCommand(firstObj, 'quantity', newQuantity, undefined, true),
    ...duplicatesToRemove.map(
      (obj) => new window.RemoveObjectCommand(window.editor!.objectByUuid(obj.uuid), true, undefined)
    ),
  ]
  window.editor!.execute(new window.MultiCmdsCommand(cmds))
}

function executeDeleteAllCommands(othersWithCode: Array<{ uuid: string }>): void {
  if (othersWithCode.length === 0) return
  const cmds = othersWithCode.map(
    (obj) => new window.RemoveObjectCommand(window.editor!.objectByUuid(obj.uuid), true, undefined)
  )
  window.editor!.execute(new window.MultiCmdsCommand(cmds))
}
